/**
 * Held-key state, for UI that reacts to a modifier being down rather than to a
 * chord being pressed.
 *
 * `useKeyHold` reads the shared `KeyStateTracker`, so a hint can light up while
 * a modifier is actually held without any component adding its own
 * keydown/keyup listeners.
 */
import {
  detectPlatform,
  useHeldKeys,
  useKeyHold,
} from '@tanstack/react-hotkeys';

/**
 * Which modifier a hint is about. `'mod'` follows the platform — Command on
 * macOS, Control elsewhere — and is what a `Mod+` binding uses.
 */
export type ModifierName = 'mod' | 'ctrl' | 'shift' | 'alt' | 'meta';

/**
 * Resolved once per page load. `navigator.platform` cannot change at runtime,
 * and `detectPlatform()` lowercases both `platform` and `userAgent` on every
 * call — not something to do inside a hook that re-runs on every key event.
 */
const MODIFIER_KEYS: Record<
  ModifierName,
  'Control' | 'Shift' | 'Alt' | 'Meta'
> = {
  mod: detectPlatform() === 'mac' ? 'Meta' : 'Control',
  ctrl: 'Control',
  shift: 'Shift',
  alt: 'Alt',
  meta: 'Meta',
};

/**
 * Whether one modifier is held.
 *
 * ONE subscription, deliberately. An earlier version returned all four
 * modifiers from a single hook, which meant a component reading only `shift`
 * re-rendered on every press *and release* of Ctrl, Alt and Meta — four
 * re-renders for a Ctrl+Shift gesture instead of two — because the returned
 * object's identity changed whenever any of them moved. Ask for the one you
 * need.
 */
export function useModifier(modifier: ModifierName): boolean {
  return useKeyHold(MODIFIER_KEYS[modifier]);
}

/**
 * Every key currently held, normalized (`'Control'`, `'Shift'`, `'A'`).
 *
 * Re-exported so callers reach the command system rather than the vendor
 * package directly — the same reason `display.ts` owns formatting.
 */
export { useHeldKeys };
